import { useNavigate, useParams } from "react-router-dom";
import { useMe } from "../../hooks/useMe";
import { useMySteps } from "../../hooks/personaQueries";
import { useWorkspaceEvents } from "../../hooks/useWorkspaceEvents";

const QUEUE_PREVIEW = 5;

export function StaffHome() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const { data: steps, isLoading } = useMySteps();
  // Filter cross-workspace results to the current workspace context for Staff —
  // they only see what's relevant inside this workspace.
  const list = (steps ?? []).filter((s) =>
    workspaceId ? s.workspaceId === workspaceId : true,
  );
  const preview = list.slice(0, QUEUE_PREVIEW);

  useWorkspaceEvents(workspaceId, () => {});

  return (
    <div className="main-inner" data-testid="staff-home">
      <div className="greet-row">
        <span className="greet">Hi, {firstName(me?.user.displayName)}.</span>
        <span className="greet-date">YOUR STEPS TODAY</span>
      </div>
      <div className="subgreet">
        <div className="section-eyebrow">YOUR QUEUE</div>
        <div className="lede">
          <b>{list.length}</b> step{list.length === 1 ? "" : "s"} assigned to you · 0 overdue.
        </div>
      </div>

      <div className="section-h">
        <span className="ttl">ASSIGNED TO YOU</span>
        {list.length > QUEUE_PREVIEW ? (
          <button
            type="button"
            className="btn"
            data-testid="staff-home-open-queue"
            onClick={() => navigate(`/workspaces/${workspaceId}/operator`)}
          >
            Open queue →
          </button>
        ) : null}
      </div>
      {isLoading ? (
        <Skeleton />
      ) : preview.length === 0 ? (
        <EmptyState message="Your queue is clear." />
      ) : (
        <div className="st-list">
          {preview.map((q) => (
            <div
              key={q.id}
              className="st-row"
              data-testid={`staff-home-step-${q.id}`}
              onClick={() =>
                navigate(`/workspaces/${q.workspaceId}/plays/${q.playId}#step-${q.id}`)
              }
            >
              <div className="st-l">
                <span className={`pd-step-type pd-type-${q.type}`}>{q.type}</span>
                <span className={`pill ${priorityPill(q.priority)}`}>{q.priority ?? "normal"}</span>
              </div>
              <div className="st-b">
                <div className="st-s">{q.title}</div>
                <div className="st-p">{q.playTitle}</div>
              </div>
              <div className="st-r">
                {q.dueAt ? <div className="st-due">due {formatDue(q.dueAt)}</div> : null}
                <button type="button" className="btn primary">
                  Open
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function firstName(displayName?: string): string {
  if (!displayName) return "there";
  return displayName.split(/\s+/)[0] ?? displayName;
}

function priorityPill(p?: "low" | "normal" | "high"): string {
  if (p === "high") return "amber";
  if (p === "low") return "gray";
  return "";
}

function formatDue(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

function Skeleton() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {[0, 1].map((i) => (
        <div
          key={i}
          style={{ height: 56, borderRadius: 8, background: "var(--bg-soft)", opacity: 0.6 }}
        />
      ))}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div
      style={{
        padding: 24,
        textAlign: "center",
        color: "var(--text-3)",
        border: "1px dashed var(--border)",
        borderRadius: 8,
        fontSize: 13,
      }}
    >
      {message}
    </div>
  );
}
